import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Plug, Wand2 } from 'lucide-react';
import { PLATFORM_LIST, PLATFORM_SPECS } from '../../shared/platforms';
import type { PlatformId, SocialAccount } from '../../shared/types';
import { PageHeader } from '../components/layout';
import { Button, ConfirmDialog, EmptyState, Skeleton, useToast } from '../components/ui';
import { ConnectionCard } from '../components/connections/ConnectionCard';
import { WordPressConnectDialog } from '../components/connections/WordPressConnectDialog';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { useWorkspaceStore } from '../stores/workspace';

/**
 * Connections — owned by WEB-4. One card per platform: connect/disconnect,
 * the WordPress app-password form, simulated accounts, and coming-soon cards
 * for TikTok/YouTube.
 */
export function ConnectionsPage() {
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pendingPlatform, setPendingPlatform] = useState<PlatformId | null>(null);
  const [wordpressOpen, setWordpressOpen] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<SocialAccount | null>(null);

  const query = useQuery({
    queryKey: qk.accounts(workspaceId),
    queryFn: () => api.accounts.list(),
    enabled: Boolean(workspaceId),
  });

  // Toast once for the ?connected=/?error= the OAuth callback redirect leaves
  // on the URL, then strip it so a refresh does not re-show it.
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    if (!connected && !error) return;

    if (connected) {
      const spec = PLATFORM_SPECS[connected as PlatformId];
      toast({
        title: `${spec?.shortName ?? connected} connected`,
        tone: 'success',
      });
      void queryClient.invalidateQueries({ queryKey: qk.accounts(workspaceId) });
    } else if (error) {
      // GET /api/accounts/callback/:platform redirects here as
      // ?error=<platformId>&message=<text>; `error_description` is only present
      // when a provider bounced us before our own handler ran.
      const spec = PLATFORM_SPECS[error as PlatformId];
      const detail = searchParams.get('message') ?? searchParams.get('error_description');
      toast({
        title: spec ? `Could not connect ${spec.shortName}` : 'Could not connect that account',
        description: detail ?? (spec ? undefined : error),
        tone: 'error',
      });
    }

    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('connected');
        next.delete('error');
        next.delete('message');
        next.delete('error_description');
        return next;
      },
      { replace: true },
    );
    // Only ever fires once per landed callback, deliberately excluding
    // `searchParams`/`setSearchParams` identity from the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const simulate = useMutation({
    mutationFn: (platform: PlatformId) => api.accounts.createSimulated({ platform }),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: qk.accounts(workspaceId) });
      toast({
        title: `${PLATFORM_SPECS[response.account.platform].shortName} simulated`,
        description: 'Publishing to it produces realistic fake results you can explore safely.',
        tone: 'success',
      });
    },
    onError: (error) => {
      toast({
        title: 'Could not create a simulated account',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const disconnect = useMutation({
    mutationFn: (accountId: string) => api.accounts.disconnect(accountId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.accounts(workspaceId) });
      toast({ title: 'Account disconnected' });
    },
    onError: (error) => {
      toast({
        title: 'Could not disconnect that account',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const startConnect = async (platform: PlatformId) => {
    setPendingPlatform(platform);
    try {
      const result = await api.accounts.connect(platform);
      if (result.mode === 'oauth') {
        window.location.href = result.authUrl;
        return;
      }
      if (result.mode === 'fields') {
        if (platform === 'wordpress') {
          setWordpressOpen(true);
        } else {
          toast({
            title: `${PLATFORM_SPECS[platform].shortName} needs a few details`,
            description: 'This platform is not wired to a connect form yet.',
            tone: 'warning',
          });
        }
        return;
      }
      if (result.canSimulate) {
        simulate.mutate(platform);
      } else {
        toast({ title: 'Not available yet', description: result.reason, tone: 'warning' });
      }
    } catch (error) {
      toast({
        title: `Could not start the ${PLATFORM_SPECS[platform].shortName} connection`,
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    } finally {
      setPendingPlatform(null);
    }
  };

  const byPlatform = useMemo(() => {
    const map = new Map<PlatformId, SocialAccount[]>();
    for (const account of query.data?.accounts ?? []) {
      map.set(account.platform, [...(map.get(account.platform) ?? []), account]);
    }
    return map;
  }, [query.data]);

  const platformStatuses = query.data?.platforms ?? [];
  const statusFor = (platform: PlatformId) => platformStatuses.find((entry) => entry.platform === platform);

  if (!workspaceId) return null;

  return (
    <>
      <PageHeader
        title="Connections"
        description="Connect the accounts you publish to. Without credentials, publishing runs in simulate mode."
      />

      {query.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-40 w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <EmptyState
          icon={<Plug className="size-5" />}
          title="Could not load connections"
          description={query.error instanceof ApiError ? query.error.message : 'Try refreshing the page.'}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {PLATFORM_LIST.map((spec) => {
            const accounts = byPlatform.get(spec.id) ?? [];
            const status = statusFor(spec.id);
            const busy = pendingPlatform === spec.id || (simulate.isPending && simulate.variables === spec.id);
            const canSimulate = status?.canSimulate ?? (!status?.configured && !spec.comingSoon);

            return (
              <ConnectionCard
                key={spec.id}
                platform={spec.id}
                accounts={accounts}
                configured={status?.configured ?? false}
                disconnectingId={disconnect.isPending ? (disconnect.variables ?? null) : null}
                onDisconnect={(account) => setDisconnectTarget(account)}
                actions={
                  spec.comingSoon ? (
                    canSimulate ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<Wand2 className="size-3.5" />}
                        loading={busy}
                        onClick={() => simulate.mutate(spec.id)}
                      >
                        Simulate
                      </Button>
                    ) : null
                  ) : (
                    <>
                      {canSimulate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Wand2 className="size-3.5" />}
                          loading={busy && simulate.isPending}
                          onClick={() => simulate.mutate(spec.id)}
                        >
                          Simulate
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        rightIcon={<ExternalLink className="size-3.5" />}
                        loading={busy && !simulate.isPending}
                        onClick={() => void startConnect(spec.id)}
                      >
                        {accounts.length > 0 ? 'Add another' : 'Connect'}
                      </Button>
                    </>
                  )
                }
              />
            );
          })}
        </div>
      )}

      <WordPressConnectDialog
        open={wordpressOpen}
        onOpenChange={setWordpressOpen}
        onConnected={() => void query.refetch()}
      />

      <ConfirmDialog
        open={Boolean(disconnectTarget)}
        onOpenChange={(next) => !next && setDisconnectTarget(null)}
        title={disconnectTarget ? `Disconnect ${disconnectTarget.displayName}?` : 'Disconnect account?'}
        description="Posts already published are unaffected. Scheduled posts using this account will fail to publish until you reconnect."
        confirmLabel="Disconnect"
        destructive
        onConfirm={async () => {
          if (!disconnectTarget) return;
          await disconnect.mutateAsync(disconnectTarget.id);
          setDisconnectTarget(null);
        }}
      />
    </>
  );
}

export default ConnectionsPage;
